/**
 * AUREXIS COMMAND HEADER
 * Military-grade top bar with real system status, live notifications, latency
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Search,
  Settings,
  LogOut,
  User,
  Zap,
  Clock,
  ChevronDown,
  Shield,
  Activity,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useServiceHealth } from '@/contexts/ServiceHealthContext';
import { PRIMARY_TABS, MORE_LINKS } from '@/components/CommandSidebar';
import { SERVICE_REGISTRY, INFRA_SERVICES } from '@/config/serviceRegistry';
import { setVisibleInterval } from '@/utils/visibleInterval';

interface Notification {
  id: number;
  time: string;
  message: string;
  level: 'info' | 'warn' | 'error';
  service: string;
}

interface CommandHeaderProps {
  isCollapsed: boolean;
}

/** Real, navigable destinations — the same ones the sidebar renders, so the
 * palette can never point somewhere the app doesn't actually go. */
interface SearchEntry {
  label: string;
  path: string;
  category: string;
}

const SEARCH_INDEX: SearchEntry[] = [
  ...PRIMARY_TABS.map((t) => ({ label: t.label, path: t.path, category: 'Navigate' })),
  ...MORE_LINKS.map((l) => ({ label: l.label, path: l.path, category: l.group })),
];

/** id -> human name, for turning a DOWN service id into a readable alert. */
const SERVICE_NAME_BY_ID: Record<string, string> = Object.fromEntries(
  [...SERVICE_REGISTRY, ...INFRA_SERVICES].map((s) => [s.id, s.name])
);

export default function CommandHeader({ isCollapsed }: CommandHeaderProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { servicesOnline, total: totalServices, statuses } = useServiceHealth();
  const [timeNow, setTimeNow] = useState(new Date());
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [systemLatency, setSystemLatency] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const notifCounter = useRef(0);
  const notifRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchPanelRef = useRef<HTMLDivElement>(null);
  const prevDownRef = useRef<Set<string> | null>(null);

  // Clock
  useEffect(() => {
    const timer = setVisibleInterval(() => setTimeNow(new Date()), 1000);
    return () => timer();
  }, []);

  // System latency probe to core-brain
  useEffect(() => {
    const probe = async () => {
      const start = performance.now();
      try {
        await fetch('/health-proxy/core-brain', { signal: AbortSignal.timeout(3000) });
        setSystemLatency(Math.round(performance.now() - start));
      } catch {
        setSystemLatency(0);
      }
    };

    probe();
    const interval = setVisibleInterval(probe, 10000);
    return () => interval();
  }, []);

  // Try fetching real events from event-bus
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch('/svc/event-bus/api/v1/events/recent', {
          signal: AbortSignal.timeout(3000),
        });
        if (res.ok) {
          const data = await res.json();
          const events = Array.isArray(data) ? data : data.events || [];
          if (events.length > 0) {
            const now = new Date().toLocaleTimeString('en-US', { hour12: false });
            const newNotifs = events.slice(0, 5).map((evt: any) => ({
              id: ++notifCounter.current,
              time: evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString('en-US', { hour12: false }) : now,
              message: evt.message || evt.type || evt.event || String(evt),
              level: (evt.level as 'info' | 'warn' | 'error') || 'info',
              service: evt.source || evt.service || 'event-bus',
            }));
            setNotifications((prev) => [...newNotifs, ...prev].slice(0, 50));
          }
        }
      } catch {
        // Event bus not available
      }
    };

    fetchEvents();
    const interval = setVisibleInterval(fetchEvents, 30000);
    return () => interval();
  }, []);

  // Real service-down alerts, sourced from the same ServiceHealthContext poll
  // that drives the header's own DEGRADED/OFFLINE badge and the sidebar's
  // online count. Fires only when the actual set of DOWN services changes,
  // so a steady-state outage doesn't spam duplicate notifications every
  // 15s poll — and it names the real services, never a placeholder count.
  useEffect(() => {
    const downIds = Object.entries(statuses)
      .filter(([, s]) => s === 'DOWN')
      .map(([id]) => id)
      .sort();
    const downKey = downIds.join(',');
    const prevKey = prevDownRef.current ? Array.from(prevDownRef.current).sort().join(',') : null;

    if (downKey === prevKey) return;
    prevDownRef.current = new Set(downIds);
    if (downIds.length === 0) return;

    const names = downIds.map((id) => SERVICE_NAME_BY_ID[id] || id);
    const alert: Notification = {
      id: ++notifCounter.current,
      time: new Date().toLocaleTimeString('en-US', { hour12: false }),
      message: `${downIds.length} service${downIds.length === 1 ? '' : 's'} down: ${names.join(', ')}`,
      level: 'error',
      service: 'service-health',
    };
    setNotifications((prev) => [alert, ...prev].slice(0, 50));
  }, [statuses]);

  // Close menus on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
      if (searchPanelRef.current && !searchPanelRef.current.contains(e.target as Node)) {
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Ctrl/Cmd+K opens (or closes) the search palette from anywhere; Escape closes it.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowSearch((v) => !v);
      } else if (e.key === 'Escape') {
        setShowSearch(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Autofocus the input and reset selection whenever the palette opens.
  useEffect(() => {
    if (showSearch) {
      setActiveIndex(0);
      const t = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    setSearchQuery('');
  }, [showSearch]);

  const userInitials = user?.username
    ? user.username.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'AD';

  const handleLogout = useCallback(async () => {
    setShowUserMenu(false);
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const unreadCount = notifications.filter((n) => n.level === 'error' || n.level === 'warn').length;

  const searchResults = (searchQuery.trim()
    ? SEARCH_INDEX.filter((e) => e.label.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : SEARCH_INDEX
  ).slice(0, 8);

  const runSearchNav = (path: string) => {
    setShowSearch(false);
    navigate(path);
  };

  return (
    <div className={`aurexis-header ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Left – System Status Badges */}
      <div className="header-status-group">
        <div className="header-status-badge">
          <span className={`dot ${servicesOnline === 0 ? 'red' : servicesOnline < totalServices ? 'amber' : 'green'}`} />
          <span style={{
            color: servicesOnline === 0 ? 'var(--mil-red)' : servicesOnline < totalServices ? 'var(--mil-amber)' : 'var(--mil-text-primary)',
            fontWeight: 600,
          }}>
            {servicesOnline === 0 ? 'OFFLINE' : servicesOnline < totalServices ? 'DEGRADED' : 'SYSTEM LIVE'}
          </span>
        </div>

        <div className="header-status-badge">
          <Clock size={12} style={{ color: 'var(--mil-text-tertiary)' }} />
          <span>{timeNow.toLocaleTimeString('en-US', { hour12: false })}</span>
        </div>

        {systemLatency > 0 && (
          <div className="header-status-badge">
            <Zap size={12} style={{ color: 'var(--mil-cyan)' }} />
            <span style={{ color: 'var(--mil-cyan)' }}>{systemLatency}ms</span>
          </div>
        )}

        <div className="header-status-badge">
          <Activity size={12} />
          <span style={{ color: servicesOnline === totalServices ? 'var(--mil-green)' : servicesOnline > 0 ? 'var(--mil-amber)' : 'var(--mil-red)' }}>
            {servicesOnline}/{totalServices} services
          </span>
        </div>
      </div>

      {/* Right – Actions */}
      <div className="header-actions">
        <button
          className="header-icon-btn"
          title="Search pages (Ctrl/Cmd+K)"
          onClick={() => setShowSearch(true)}
        >
          <Search size={16} />
        </button>

        {showSearch && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 100,
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '12vh',
          }}>
            <div ref={searchPanelRef} style={{
              width: '480px',
              maxWidth: '90vw',
              maxHeight: '70vh',
              background: 'var(--mil-bg-card)',
              border: '1px solid var(--mil-border)',
              borderRadius: 'var(--mil-radius-lg)',
              boxShadow: 'var(--mil-shadow-lg)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: 'fit-content',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                borderBottom: '1px solid var(--mil-border)',
              }}>
                <Search size={14} style={{ color: 'var(--mil-text-tertiary)', flexShrink: 0 }} />
                <input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setActiveIndex(0); }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setActiveIndex((i) => Math.min(i + 1, searchResults.length - 1));
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setActiveIndex((i) => Math.max(i - 1, 0));
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      const target = searchResults[activeIndex];
                      if (target) runSearchNav(target.path);
                    }
                  }}
                  placeholder="Search pages — Dashboard, Journal, Admin Ops..."
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: 'var(--mil-text-primary)',
                    fontSize: '0.8125rem',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                />
                <span style={{
                  fontSize: '0.5625rem',
                  color: 'var(--mil-text-muted)',
                  border: '1px solid var(--mil-border)',
                  borderRadius: '4px',
                  padding: '0.0625rem 0.25rem',
                  flexShrink: 0,
                }}>
                  ESC
                </span>
              </div>
              <div style={{ overflowY: 'auto' }}>
                {searchResults.length === 0 ? (
                  <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--mil-text-muted)', fontSize: '0.75rem' }}>
                    No pages match &quot;{searchQuery}&quot;
                  </div>
                ) : (
                  searchResults.map((r, i) => (
                    <button
                      key={r.path}
                      onClick={() => runSearchNav(r.path)}
                      onMouseEnter={() => setActiveIndex(i)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem 1rem',
                        background: i === activeIndex ? 'var(--mil-bg-hover)' : 'transparent',
                        border: 'none',
                        color: 'var(--mil-text-secondary)',
                        fontSize: '0.8125rem',
                        fontFamily: "'JetBrains Mono', monospace",
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span>{r.label}</span>
                      <span style={{
                        fontSize: '0.5625rem',
                        color: 'var(--mil-text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        flexShrink: 0,
                        marginLeft: '0.75rem',
                      }}>
                        {r.category}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notifications — LIVE */}
        <div ref={notifRef} style={{ position: 'relative' }}>
          <button
            className="header-icon-btn"
            title="Notifications"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <Bell size={16} />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: 'var(--mil-red)',
                color: '#fff',
                fontSize: '0.5625rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: '0.5rem',
              background: 'var(--mil-bg-card)',
              border: '1px solid var(--mil-border)',
              borderRadius: 'var(--mil-radius-lg)',
              width: '360px',
              maxHeight: '400px',
              boxShadow: 'var(--mil-shadow-lg)',
              zIndex: 50,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.625rem 0.875rem',
                borderBottom: '1px solid var(--mil-border)',
              }}>
                <span style={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--mil-text-secondary)' }}>
                  Notifications ({notifications.length})
                </span>
                <button
                  onClick={() => setNotifications([])}
                  style={{ background: 'none', border: 'none', color: 'var(--mil-text-muted)', fontSize: '0.625rem', cursor: 'pointer' }}
                >
                  Clear all
                </button>
              </div>
              <div style={{ overflowY: 'auto', maxHeight: '320px' }}>
                {notifications.length === 0 ? (
                  <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--mil-text-muted)', fontSize: '0.75rem' }}>
                    No notifications
                  </div>
                ) : (
                  notifications.map((n) => (
                    <div key={n.id} style={{
                      display: 'flex',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      borderBottom: '1px solid var(--mil-border)',
                      fontSize: '0.6875rem',
                    }}>
                      <span style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        marginTop: 4,
                        flexShrink: 0,
                        background: n.level === 'error' ? 'var(--mil-red)' : n.level === 'warn' ? 'var(--mil-amber)' : 'var(--mil-cyan)',
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ color: 'var(--mil-text-secondary)' }}>{n.message}</div>
                        <div style={{ color: 'var(--mil-text-muted)', fontSize: '0.5625rem', marginTop: '0.125rem' }}>
                          {n.service} · {n.time}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <button className="header-icon-btn" title="Settings" onClick={() => navigate('/settings')}>
          <Settings size={16} />
        </button>

        {/* User Menu */}
        <div style={{ position: 'relative' }}>
          <button
            className="header-user-menu"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className="header-user-avatar">{userInitials}</div>
            <div className="header-user-info">
              <span className="header-user-name">{user?.username || 'Admin'}</span>
              <span className="header-user-role">{user?.role || 'System Admin'}</span>
            </div>
            <ChevronDown size={12} style={{ color: 'var(--mil-text-tertiary)' }} />
          </button>

          {showUserMenu && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: '0.5rem',
              background: 'var(--mil-bg-card)',
              border: '1px solid var(--mil-border)',
              borderRadius: 'var(--mil-radius-lg)',
              minWidth: '200px',
              boxShadow: 'var(--mil-shadow-lg)',
              zIndex: 50,
              overflow: 'hidden',
            }}>
              <button
                onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.625rem 1rem', background: 'transparent', border: 'none',
                  color: 'var(--mil-text-secondary)', fontSize: '0.8125rem', cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--mil-bg-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <User size={14} />
                <span>Profile & Settings</span>
              </button>
              <div style={{ borderTop: '1px solid var(--mil-border)' }}>
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.625rem 1rem', background: 'transparent', border: 'none',
                    color: 'var(--mil-red)', fontSize: '0.8125rem', cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--mil-bg-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <LogOut size={14} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { CommandHeader };
